<template>
  <div class="list-page-container">
    <!-- 统一控制中心：标题与返回 -->
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <el-button icon="el-icon-back" circle size="small" @click="$router.back()" class="back-btn"></el-button>
          <i class="el-icon-tickets"></i>
          <span>{{ $t('system.jobLogTips') }}</span>
        </div>
      </div>
    </div>

    <!-- 统一控制中心：筛选模块 -->
    <div class="control-panel has-query">
      <div class="control-container">
        <div class="filter-wrapper">
          <div class="filter-content">
            <el-form :inline="true" :model="queryParams" class="filter-form">
              <el-form-item :label="$t('system.jobId')">
                <el-input 
                  v-model="queryParams.jobId" 
                  :placeholder="$t('system.inputJobId')" 
                  clearable 
                  prefix-icon="el-icon-search"
                  @keyup.enter.native="handleSearch" 
                />
              </el-form-item>
              <el-form-item class="search-btns">
                <el-button type="primary" icon="el-icon-search" @click="handleSearch">{{ $t('common.query') }}</el-button>
                <el-button icon="el-icon-refresh" @click="resetQuery">{{ $t('common.reset') }}</el-button>
              </el-form-item>
            </el-form>
          </div>
        </div>
      </div>
    </div>

    <!-- Log List Table -->
    <div class="table-container">
      <el-table
        :data="logList"
        v-loading="loading"
        class="custom-table"
        style="width: 100%"
        height="100%"
      >
        <el-table-column prop="id" :label="$t('system.logId')" width="180" align="center" />
        <el-table-column prop="jobId" :label="$t('system.jobId')" width="180" align="center" />
        <el-table-column prop="beanName" :label="$t('system.beanName')" min-width="150" align="center" />
        <el-table-column prop="params" :label="$t('system.jobParams')" min-width="150" show-overflow-tooltip align="center" />
        <el-table-column prop="status" :label="$t('common.status')" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 0 ? 'success' : 'danger'" size="mini" effect="plain">
              {{ scope.row.status === 0 ? $t('common.success') : $t('common.fail') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="times" :label="$t('system.consumeTime')" width="120" align="center">
          <template slot-scope="scope">
            <span class="consume-time">{{ scope.row.times }}ms</span>
          </template>
        </el-table-column>
        <el-table-column prop="createDate" :label="$t('system.executionTime')" width="160" align="center" />
        <el-table-column v-if="hasAnyActionPermission" :label="$t('common.operations')" :width="actionColumnWidth" align="center" fixed="right">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button type="primary" size="mini" plain icon="el-icon-view" @click="handleDetail(scope.row)">
                {{ $t('common.detail') }}
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="queryParams.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="queryParams.limit"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
        />
      </div>
    </div>

    <!-- Detail Dialog -->
    <el-dialog :title="$t('system.jobLogDetail')" :visible.sync="detailVisible" width="600px" append-to-body custom-class="custom-dialog">
      <el-descriptions :column="1" border size="medium">
        <el-descriptions-item :label="$t('system.logId')">{{ detailInfo.id }}</el-descriptions-item>
        <el-descriptions-item :label="$t('system.jobId')">{{ detailInfo.jobId }}</el-descriptions-item>
        <el-descriptions-item :label="$t('system.beanName')">{{ detailInfo.beanName }}</el-descriptions-item>
        <el-descriptions-item :label="$t('system.jobParams')">{{ detailInfo.params }}</el-descriptions-item>
        <el-descriptions-item :label="$t('common.status')">
          <el-tag :type="detailInfo.status === 0 ? 'success' : 'danger'" size="small">
            {{ detailInfo.status === 0 ? $t('common.success') : $t('common.fail') }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('system.consumeTime')">{{ detailInfo.times }}ms</el-descriptions-item>
        <el-descriptions-item :label="$t('system.executionTime')">{{ detailInfo.createDate }}</el-descriptions-item>
        <el-descriptions-item v-if="detailInfo.status === 1" :label="$t('system.errorMessage')">
          <div class="error-msg">{{ detailInfo.error }}</div>
        </el-descriptions-item>
      </el-descriptions>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false" size="medium">{{ $t('common.close') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getJobLogList } from '@/apis/system/job'

export default {
  name: 'JobLog',
  data() {
    return {
      loading: false,
      logList: [],
      total: 0,
      queryParams: {
        page: 1,
        limit: 10,
        jobId: ''
      },
      detailVisible: false,
      detailInfo: {}
    }
  },
  created() {
    this.getList()
  },
  computed: {
    visibleActionCount() {
      return 1 // 详情按钮始终显示
    },
    hasAnyActionPermission() {
      return this.visibleActionCount > 0
    },
    actionColumnWidth() {
      const count = this.visibleActionCount
      const widthMap = { 1: 100, 2: 170 }
      return widthMap[count] || 0
    }
  },
  methods: {
    getList() {
      this.loading = true
      getJobLogList(this.queryParams).then(res => {
        this.logList = res.data.list
        this.total = res.data.total
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },
    handleSearch() {
      this.queryParams.page = 1
      this.getList()
    },
    resetQuery() {
      this.queryParams = {
        page: 1,
        limit: 10,
        jobId: ''
      }
      this.handleSearch()
    },
    handleSizeChange(val) {
      this.queryParams.limit = val
      this.getList()
    },
    handleCurrentChange(val) {
      this.queryParams.page = val
      this.getList()
    },
    handleDetail(row) {
      this.detailInfo = row
      this.detailVisible = true
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/list-page.scss";

/* 任务日志页面特有样式 */
.list-page-container {
  .header-info {
    .back-btn {
      border: 1px solid #e2e8f0;
      &:hover {
        background-color: #f1f5f9;
        color: #409eff;
      }
    }
  }

  .filter-form {
    :deep(.el-form-item) {
      .el-form-item__label {
        padding: 0 12px 0 0;
        color: #64748b;
        font-weight: 500;
      }
    }
  }

  .table-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  :deep(.custom-table) {
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #f1f5f9;

    &:hover {
      box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.05);
    }

    .el-table__header-wrapper th {
      background-color: #f8fafc !important;
      color: #475569 !important;
      font-weight: 600 !important;
      height: 50px;
      padding: 0;
    }

    .el-table__row {
      height: 48px;
      td {
        padding: 0;
        color: #1e293b;
      }

      &:hover > td {
        background-color: #f8fafc !important;
      }
    }

    &.el-table--striped .el-table__row--striped td {
      background-color: #fcfdfe;
    }

    .consume-time {
      color: #64748b;
      font-family: monospace;
      font-weight: 500;
    }
  }

  :deep(.custom-dialog) {
    border-radius: 12px;
    overflow: hidden;

    .el-dialog__header {
      background-color: #f8fafc;
      border-bottom: 1px solid #f1f5f9;
      padding: 20px;

      .el-dialog__title {
        font-size: 16px;
        font-weight: 600;
        color: #1e293b;
      }
    }

    .el-dialog__body {
      padding: 24px;
    }

    .el-dialog__footer {
      padding: 15px 24px 24px;
      border-top: 1px solid #f1f5f9;
    }
  }

  :deep(.el-descriptions) {
    .el-descriptions-item__label {
      width: 120px;
      background-color: #f8fafc;
      color: #64748b;
      font-weight: 500;
    }
  }

  .error-msg {
    color: #ef4444;
    word-break: break-all;
    max-height: 200px;
    overflow-y: auto;
    font-family: monospace;
    font-size: 13px;
    line-height: 1.6;
    padding: 12px;
    background-color: #fef2f2;
    border-radius: 6px;
    border: 1px solid #fee2e2;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: #fca5a5;
      border-radius: 2px;
    }
  }
}

@media screen and (max-width: 1024px) {
  .list-page-container .filter-form {
    flex-direction: column;
    align-items: stretch;

    .el-form-item {
      width: 100%;
      .el-input { width: 100% !important; }
    }
  }

  .list-page-container .search-btns {
    margin-left: 0;
    margin-top: 10px;
    justify-content: flex-end;
  }
}
</style>
